/* Progressive enhancement. Product and cart forms remain native Shopify forms. */
(() => {
  'use strict';
  const focusable = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary';
  const visible = el => el.getClientRects().length > 0;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const animations = new Set();
  const ease = 'cubic-bezier(.22, 1, .36, 1)';
  const animate = (element, keyframes, options = {}) => {
    if (!element || reducedMotion.matches || !element.animate) return;
    const animation = element.animate(keyframes, {duration: 500, easing: ease, ...options});
    animations.add(animation);
    animation.finished.then(() => animations.delete(animation), () => animations.delete(animation));
    return animation;
  };
  reducedMotion.addEventListener('change', event => {
    if (event.matches) animations.forEach(animation => animation.cancel());
  });
  const revealSelector = '.hero__copy > *, .hero__visual, .section-head, .product-card, .editorial__intro, .editorial__item, .image-text__media, .image-text__copy, .newsletter__inner > *, .pdp__gallery, .pdp__info > .eyebrow, .pdp__info > h1, .pdp__form, .review, .cart-item, .cart-summary, .collection-hero, .prose-page';
  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      revealObserver.unobserve(entry.target);
      animate(entry.target, [{opacity: 0, transform: 'translateY(18px)'}, {opacity: 1, transform: 'translateY(0)'}],
        {duration: 650, delay: Number(entry.target.dataset.motionDelay || 0), fill: 'backwards'});
    });
  }, {threshold: .08}) : null;
  function initMotion(root) {
    if (!revealObserver || reducedMotion.matches) return;
    root.querySelectorAll(revealSelector).forEach(element => {
      if (element.dataset.motionReady) return;
      element.dataset.motionReady = 'true';
      const siblings = [...element.parentElement.children].filter(el => el.matches(revealSelector));
      element.dataset.motionDelay = Math.min(Math.max(0, siblings.indexOf(element)), 4) * 55;
      revealObserver.observe(element);
    });
  }
  const imageMotionSelector = '.hero__image, .product-card__media img, .image-text__media img, .collection-tile__media img, .pdp__slide img, .cart-item__image img, .cart-confirmation__media img';
  function initImageMotion(root) {
    root.querySelectorAll(imageMotionSelector).forEach(image => {
      if (image.dataset.imageMotionReady) return;
      image.dataset.imageMotionReady = 'true';
      image.classList.add('image-reveal');
      const markLoaded = () => image.classList.add('is-loaded');
      if (image.complete && image.naturalWidth) markLoaded();
      else {
        image.addEventListener('load', markLoaded, {once:true});
        image.addEventListener('error', markLoaded, {once:true});
      }
    });
  }
  function initScrollProgress() {
    const bar = document.querySelector('[data-scroll-progress]');
    if (!bar || bar.dataset.initialized) return;
    bar.dataset.initialized = 'true';
    let frame = 0;
    const update = () => {
      frame = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.transform = 'scaleX(' + (max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0) + ')';
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    window.addEventListener('scroll', schedule, {passive:true});
    window.addEventListener('resize', schedule, {passive:true});
    update();
  }
  const setMessage = (node, text, cartURL) => {
    node.replaceChildren(document.createTextNode(text));
    if (cartURL) { const link = document.createElement('a'); link.href = cartURL; link.textContent = 'View bag'; node.append(link); }
  };

  function updateDiscountProgress(cart) {
    if (!Number.isFinite(cart.item_count)) return;
    const count = Math.min(2, Math.max(0, cart.item_count));
    document.querySelectorAll('[data-discount-progress]').forEach(panel => {
      const message = count === 2 ? "You've unlocked 15% off" : count === 1 ? 'Add 1 more perfume to unlock 15% off' : 'Add 2 perfumes to unlock 15% off';
      const label = panel.querySelector('[data-discount-message]');
      if (label.textContent !== message) label.textContent = message;
      panel.classList.toggle('is-unlocked', count === 2);
      panel.querySelector('[data-discount-fill]').style.width = (count * 50) + '%';
      const track = panel.querySelector('[data-discount-track]');
      track.setAttribute('aria-valuenow', count);
      track.setAttribute('aria-valuetext', count === 2 ? '15% off unlocked' : count + ' of 2 items');
    });
  }

  function initCartConfirmation() {
    const dialog = document.querySelector('[data-cart-confirmation]');
    if (!dialog || dialog.dataset.initialized) return;
    dialog.dataset.initialized = 'true';
    let closeTimer;
    const status = dialog.querySelector('[data-cart-confirmation-status]');
    const close = () => {
      if (!dialog.open || dialog.classList.contains('is-closing')) return;
      if (reducedMotion.matches) { dialog.close(); return; }
      dialog.classList.add('is-closing');
      closeTimer = setTimeout(() => dialog.close(), 260);
    };
    dialog.querySelectorAll('[data-cart-confirmation-close]').forEach(button => button.addEventListener('click', close));
    dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
    });
    dialog.addEventListener('close', () => {
      clearTimeout(closeTimer);
      dialog.classList.remove('is-closing');
      document.body.classList.remove('cart-confirmation-open');
    });
    dialog.addEventListener('click', async event => {
      const remove = event.target.closest('[data-cart-confirmation-remove]');
      if (!remove) return;
      const key = remove.dataset.key;
      const endpoint = dialog.dataset.cartChangeUrl;
      if (!key || !endpoint || remove.disabled) return;
      remove.disabled = true;
      remove.setAttribute('aria-busy', 'true');
      status.hidden = true;
      const item = remove.closest('.cart-confirmation__item');
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {'Content-Type':'application/json', Accept:'application/json'},
          body: JSON.stringify({id:key, quantity:0})
        });
        if (!response.ok) throw new Error('Unable to remove');
        const cart = await response.json();
        updateDiscountProgress(cart);
        item?.classList.add('is-removing');
        setTimeout(() => renderCartDrawer(cart), reducedMotion.matches ? 0 : 220);
      } catch (_) {
        remove.disabled = false;
        remove.removeAttribute('aria-busy');
        status.textContent = 'We could not update your bag. Please try again.';
        status.hidden = false;
      }
    });
    document.addEventListener('click', event => {
      const bagLink = event.target.closest('.bag-link');
      if (!bagLink || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      openCartDrawer();
    });
  }

  const formatCartMoney = (cents, currency) => {
    const value = cents / 100;
    try {
      const number = new Intl.NumberFormat(document.documentElement.lang || 'en', {minimumFractionDigits:0, maximumFractionDigits:2}).format(value);
      return (currency === 'EGP' || !currency ? 'LE ' : currency + ' ') + number;
    } catch (_) { return (currency === 'EGP' || !currency ? 'LE ' : currency + ' ') + String(value); }
  };

  function renderCartDrawer(cart, added) {
    const dialog = document.querySelector('[data-cart-confirmation]');
    if (!dialog) return;
    const items = dialog.querySelector('[data-cart-drawer-items]');
    items.replaceChildren();
    cart.items.forEach(line => {
      const row = document.createElement('article');
      row.className = 'cart-confirmation__item';
      const media = document.createElement('a');
      media.className = 'cart-confirmation__media'; media.href = line.url;
      if (line.image) { const image = document.createElement('img'); image.src = line.image; image.alt = line.product_title; image.width = 180; image.height = 225; media.append(image); }
      const details = document.createElement('div'); details.className = 'cart-confirmation__details';
      const title = document.createElement('h3'); const titleLink = document.createElement('a'); titleLink.href = line.url; titleLink.textContent = line.product_title; title.append(titleLink);
      const meta = document.createElement('p'); meta.textContent = (line.variant_title && line.variant_title !== 'Default Title' ? line.variant_title + ' · ' : '') + 'Qty ' + line.quantity;
      const price = document.createElement('div'); price.className = 'cart-confirmation__price'; price.textContent = formatCartMoney(line.final_line_price, cart.currency);
      details.append(title, meta, price);
      const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'icon-button cart-confirmation__remove'; remove.dataset.cartConfirmationRemove = ''; remove.dataset.key = line.key; remove.setAttribute('aria-label', 'Remove ' + line.product_title + ' from bag'); remove.textContent = '×';
      row.append(media, details, remove); items.append(row);
    });
    const empty = dialog.querySelector('[data-cart-drawer-empty]');
    const footer = dialog.querySelector('[data-cart-drawer-footer]');
    empty.hidden = cart.item_count > 0; footer.hidden = cart.item_count === 0;
    dialog.querySelector('[data-cart-drawer-summary]').textContent = cart.item_count ? cart.item_count + (cart.item_count === 1 ? ' fragrance selected' : ' fragrances selected') : '';
    dialog.querySelector('[data-cart-drawer-total]').textContent = formatCartMoney(cart.total_price, cart.currency);
    const mark = dialog.querySelector('[data-cart-added-mark]'); mark.hidden = !added;
    dialog.querySelector('[data-cart-drawer-eyebrow]').textContent = added ? 'Added to your bag' : 'Your shopping bag';
    dialog.querySelector('[data-cart-drawer-title]').textContent = added ? 'A beautiful choice.' : 'Your bag.';
    document.querySelectorAll('[data-cart-count]').forEach(count => {
      count.textContent = cart.item_count;
      count.closest('a')?.setAttribute('aria-label', 'Shopping bag, ' + cart.item_count + (cart.item_count === 1 ? ' item' : ' items'));
    });
    updateDiscountProgress(cart);
    initImageMotion(items);
  }

  async function openCartDrawer(added = false) {
    const dialog = document.querySelector('[data-cart-confirmation]');
    if (!dialog?.showModal) return;
    const response = await fetch(dialog.dataset.cartUrl, {headers:{Accept:'application/json'}});
    if (!response.ok) { window.location.href = '/cart'; return; }
    renderCartDrawer(await response.json(), added);
    document.querySelectorAll('[data-overlay-details][open]').forEach(details => { details.open = false; });
    if (!dialog.open) {
      dialog.classList.remove('is-closing');
      dialog.showModal();
      document.body.classList.add('cart-confirmation-open');
    }
  }

  function showCartConfirmation(item, count, source) {
    const bag = document.querySelector('.bag-link');
    if (!bag) return;
    const start = (source || document.querySelector('.pdp__slide:not([hidden]) img'))?.getBoundingClientRect();
    const end = bag.getBoundingClientRect();
    if (start && !reducedMotion.matches) {
      const flyer = document.createElement('span');
      flyer.className = 'cart-fly';
      if (item?.image) {
        const image = document.createElement('img'); image.src = item.image; image.alt = ''; flyer.append(image);
      }
      document.body.append(flyer);
      const size = Math.min(72, Math.max(48, Math.min(start.width, start.height) * .22));
      flyer.style.cssText = 'width:' + size + 'px;height:' + size + 'px;left:' + (start.left + start.width / 2 - size / 2) + 'px;top:' + (start.top + start.height / 2 - size / 2) + 'px';
      const dx = end.left + end.width / 2 - (start.left + start.width / 2);
      const dy = end.top + end.height / 2 - (start.top + start.height / 2);
      const flight = flyer.animate([
        {transform:'translate(0,0) scale(1)', opacity:1, offset:0},
        {transform:'translate(' + (dx * .45) + 'px,' + (dy * .18 - 55) + 'px) scale(.82)', opacity:1, offset:.42},
        {transform:'translate(' + dx + 'px,' + dy + 'px) scale(.22)', opacity:.2, offset:1}
      ], {duration:720, easing:'cubic-bezier(.32,.72,.22,1)', fill:'forwards'});
      flight.finished.finally(() => flyer.remove());
    }
    bag.classList.remove('is-receiving');
    requestAnimationFrame(() => bag.classList.add('is-receiving'));
    setTimeout(() => bag.classList.remove('is-receiving'), 900);
    bag.setAttribute('aria-label', 'Shopping bag, ' + (Number.isFinite(count) ? count : '') + ' items. Product added.');
  }

  function initHeader(root = document) {
    root.querySelectorAll('[data-overlay-details]').forEach(details => {
      if (details.dataset.initialized) return;
      details.dataset.initialized = 'true';
      const summary = details.querySelector('summary');
      const panel = details.querySelector('.menu-panel');
      const updatePanelPosition = () => {
        if (!panel || !window.matchMedia('(max-width: 800px)').matches) return;
        const header = details.closest('.header');
        if (header) panel.style.setProperty('--mobile-menu-top', Math.round(header.getBoundingClientRect().bottom) + 'px');
      };
      if (panel) window.addEventListener('resize', updatePanelPosition, {passive:true});
      const close = () => { if (details.open) { details.open = false; summary.focus(); } };
      details.querySelectorAll('[data-close-details]').forEach(button => button.addEventListener('click', close));
      details.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', String(details.open));
        if (!details.open) return;
        updatePanelPosition();
        document.querySelectorAll('[data-overlay-details][open]').forEach(other => { if (other !== details) other.open = false; });
        const input = details.querySelector('input, nav a');
        if (input) input.focus({preventScroll:true});
        animate(details.querySelector('.menu-panel, .search-panel'), [{opacity: 0, transform: 'translateY(-10px)'}, {opacity: 1, transform: 'translateY(0)'}], {duration: 350});
      });
      details.addEventListener('keydown', event => {
        if (!details.open) return;
        if (event.key === 'Escape') { event.preventDefault(); close(); }
        if (event.key !== 'Tab') return;
        const items = [...details.querySelectorAll(focusable)].filter(visible);
        const first = items[0], last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      });
    });
  }

  function initProducts(root = document) {
    root.querySelectorAll('[data-product-section]').forEach(section => {
      if (section.dataset.initialized) return;
      section.dataset.initialized = 'true';
      const variants = JSON.parse(section.querySelector('[data-variant-data]').textContent);
      const form = section.querySelector('form[action*="/cart/add"]');
      const variantInput = form.querySelector('[data-variant-input]');
      const quantity = form.querySelector('[data-product-quantity]');
      const add = form.querySelector('[data-add-button]');
      const addLabel = add.querySelector('[data-add-label]');
      const dynamic = form.querySelector('[data-dynamic-checkout]');
      const message = form.querySelector('[data-product-message]');
      const slides = [...section.querySelectorAll('[data-slide-index]')];
      const thumbs = [...section.querySelectorAll('[data-thumb-index]')];
      let active = Math.max(0, slides.findIndex(slide => !slide.hidden));
      let busy = false;
      let added = false;
      let feedbackTimer;
      const currentVariant = () => variants.find(variant => String(variant.id) === variantInput.value);
      function gallery(index) {
        if (!slides.length) return;
        active = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          slide.hidden = i !== active;
          if (slide.hidden) slide.querySelectorAll('video').forEach(video => video.pause());
        });
        thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === active)));
        animate(slides[active], [{opacity: .3, transform: 'translateX(8px)'}, {opacity: 1, transform: 'translateX(0)'}], {duration: 400});
      }
      const sync = (updateURL = false) => {
        if (updateURL) { added = false; clearTimeout(feedbackTimer); }
        const variant = currentVariant();
        add.disabled = busy || !variant?.available;
        addLabel.textContent = busy ? 'Adding…' : variant?.available ? (added ? 'Added to bag' : 'Add to bag') : 'Sold out';
        add.classList.toggle('is-loading', busy);
        add.classList.toggle('is-added', added && !busy);
        if (dynamic) { dynamic.hidden = !variant?.available; dynamic.inert = busy; }
        if (!variant) { variantInput.disabled = true; return; }
        variantInput.disabled = false;
        const price = section.querySelector('[data-price-for="' + variant.id + '"]');
        if (price) section.querySelector('[data-product-price]').replaceChildren(price.content.cloneNode(true));
        quantity.min = variant.min; quantity.step = variant.step;
        if (variant.max !== null) quantity.max = variant.max; else quantity.removeAttribute('max');
        const value = Number(quantity.value);
        if (value < variant.min || (variant.max !== null && value > variant.max) || (value - variant.min) % variant.step) quantity.value = variant.min;
        if (updateURL) {
          setMessage(message, variant.available ? '' : 'This selection is currently sold out.');
          const url = new URL(location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url);
          if (variant.mediaId) { const index = slides.findIndex(slide => slide.dataset.mediaId === String(variant.mediaId)); if (index >= 0) gallery(index); }
        }
      };
      variantInput.addEventListener('change', () => sync(true));
      section.querySelector('[data-quantity-minus]').addEventListener('click', () => { if (Number(quantity.value) > Number(quantity.min)) quantity.stepDown(); });
      section.querySelector('[data-quantity-plus]').addEventListener('click', () => quantity.stepUp());
      section.querySelector('[data-gallery-prev]')?.addEventListener('click', () => gallery(active - 1));
      section.querySelector('[data-gallery-next]')?.addEventListener('click', () => gallery(active + 1));
      thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => gallery(i)));
      const track = section.querySelector('[data-slides]');
      let start = null;
      track.addEventListener('touchstart', event => { start = {x:event.changedTouches[0].clientX, y:event.changedTouches[0].clientY}; }, {passive:true});
      track.addEventListener('touchend', event => {
        if (!start || event.target.closest('video, iframe, model-viewer')) return;
        const dx = event.changedTouches[0].clientX - start.x, dy = event.changedTouches[0].clientY - start.y;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) gallery(active + (dx < 0 ? 1 : -1));
        start = null;
      }, {passive:true});
      form.addEventListener('submit', async event => {
        if (event.submitter && event.submitter !== add) return;
        event.preventDefault();
        if (busy) return;
        const variant = currentVariant();
        if (!variant?.available) { setMessage(message, 'This selection is currently sold out.'); sync(); return; }
        if (!form.reportValidity()) return;
        const body = new FormData(form);
        const selection = {
          title: section.querySelector('h1').textContent.trim(),
          variant: variantInput.tagName === 'SELECT' ? variantInput.selectedOptions[0].textContent.trim() : '',
          quantity: Number(body.get('quantity')) || 1,
          variantId: variant.id,
          key: '',
          image: section.querySelector('.pdp__slide:not([hidden]) img')?.currentSrc || '',
          price: document.createDocumentFragment(),
          cartURL: section.dataset.cartUrl
        };
        section.querySelector('[data-product-price]').childNodes.forEach(node => selection.price.append(node.cloneNode(true)));
        let confirmed = false;
        let cartCount;
        clearTimeout(feedbackTimer); added = false;
        busy = true; sync(); setMessage(message, ''); form.setAttribute('aria-busy', 'true');
        try {
          const response = await fetch(section.dataset.cartAddUrl, {method:'POST', headers:{Accept:'application/json'}, body});
          const data = await response.json();
          if (!response.ok) { setMessage(message, typeof data.description === 'string' ? data.description : 'This item could not be added. Please check your selection.'); return; }
          selection.key = data.key || data.items?.[0]?.key || '';
          confirmed = true;
          setMessage(message, 'Added to your bag. ', section.dataset.cartUrl);
          try {
            const cartResponse = await fetch(section.dataset.cartUrl + '.js', {headers:{Accept:'application/json'}});
            if (cartResponse.ok) {
              const cart = await cartResponse.json();
              updateDiscountProgress(cart);
              cartCount = cart.item_count;
              selection.key = selection.key || cart.items?.find(item => String(item.variant_id) === String(selection.variantId))?.key || '';
              document.querySelectorAll('[data-cart-count]').forEach(count => {
                count.textContent = cart.item_count;
                count.closest('a')?.setAttribute('aria-label', 'Shopping bag, ' + cart.item_count + (cart.item_count === 1 ? ' item' : ' items'));
                animate(count, [{opacity: 0, transform: 'translateY(6px)'}, {opacity: 1, transform: 'translateY(0)'}], {duration: 350});
                animate(count.closest('a')?.querySelector('svg'), [{transform:'scale(1)'}, {transform:'scale(1.16) rotate(-5deg)',offset:.4}, {transform:'scale(1) rotate(0)'}], {duration: 550});
              });
            }
          } catch (_) { /* Bag page remains authoritative if the optional count refresh fails. */ }
        } catch (_) {
          setMessage(message, 'We could not confirm the update. Check your bag before trying again. ', section.dataset.cartUrl);
        } finally {
          busy = false; added = confirmed; sync(); form.removeAttribute('aria-busy');
          if (confirmed) {
            showCartConfirmation(selection, cartCount, section.querySelector('.pdp__slide:not([hidden]) img') || add);
            feedbackTimer = setTimeout(() => { added = false; sync(); }, 2200);
          }
        }
      });
      sync();
      section.querySelectorAll('[data-product-description] p').forEach(p => {
        if (!p.textContent.trim() && !p.querySelector('img,video,iframe')) p.hidden = true;
      });
    });
  }

  function initCardAdds(root = document) {
    root.querySelectorAll('[data-card-add]').forEach(form => {
      if (form.dataset.initialized) return;
      form.dataset.initialized = 'true';
      const button = form.querySelector('button');
      const label = form.querySelector('[data-card-add-label]');
      const originalLabel = label.textContent;
      let busy = false;
      let resetTimer;
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (busy) return;
        busy = true;
        clearTimeout(resetTimer);
        button.disabled = true;
        form.classList.remove('is-added', 'has-error');
        form.classList.add('is-loading');
        label.textContent = 'Adding…';
        const body = new FormData(form);
        let confirmed = false;
        let cartCount;
        try {
          const response = await fetch(form.action, {method:'POST', headers:{Accept:'application/json'}, body});
          const data = await response.json();
          if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : 'Unable to add');
          const variantId = body.get('id');
          let cardKey = data.key || data.items?.[0]?.key || '';
          confirmed = true;
          label.textContent = 'Added';
          form.classList.add('is-added');
          try {
            const cartResponse = await fetch(form.dataset.cartUrl + '.js', {headers:{Accept:'application/json'}});
            if (cartResponse.ok) {
              const cart = await cartResponse.json();
              updateDiscountProgress(cart);
              cartCount = cart.item_count;
              cardKey = cardKey || cart.items?.find(item => String(item.variant_id) === String(variantId))?.key || '';
              document.querySelectorAll('[data-cart-count]').forEach(count => {
                count.textContent = cart.item_count;
                count.closest('a')?.setAttribute('aria-label', 'Shopping bag, ' + cart.item_count + (cart.item_count === 1 ? ' item' : ' items'));
                animate(count, [{opacity: 0, transform: 'translateY(6px)'}, {opacity: 1, transform: 'translateY(0)'}], {duration: 350});
                animate(count.closest('a')?.querySelector('svg'), [{transform:'scale(1)'}, {transform:'scale(1.16) rotate(-5deg)',offset:.4}, {transform:'scale(1) rotate(0)'}], {duration: 550});
              });
            }
          } catch (_) { /* The bag page remains authoritative if the optional count refresh fails. */ }
          const price = document.createDocumentFragment();
          price.append(document.createTextNode(form.dataset.price || ''));
          showCartConfirmation({
            title: form.dataset.title || 'Fragrance',
            variant: '',
            quantity: 1,
            variantId,
            key: cardKey,
            image: form.dataset.image || '',
            price,
            cartURL: form.dataset.cartUrl
          }, cartCount, form.closest('.product-card, .mini-perfume')?.querySelector('img') || button);
        } catch (_) {
          label.textContent = 'Try again';
          form.classList.add('has-error');
        } finally {
          busy = false;
          button.disabled = false;
          form.classList.remove('is-loading');
          if (confirmed) resetTimer = setTimeout(() => { form.classList.remove('is-added'); label.textContent = originalLabel; }, 2200);
          else resetTimer = setTimeout(() => { form.classList.remove('has-error'); label.textContent = originalLabel; }, 1800);
        }
      });
    });
  }

  function initMiniPerfumes(root) {
    root.querySelectorAll('[data-mini-perfumes]').forEach(panel => {
      if (panel.dataset.initialized) return;
      panel.dataset.initialized = 'true';
      const track = panel.querySelector('.mini-perfumes__track');
      if (track.children.length < 2) return;
      panel.querySelector('[data-mini-controls]').hidden = false;
      const move = direction => {
        const rtl = getComputedStyle(track).direction === 'rtl';
        const step = track.children[0].getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap);
        const offset = Math.abs(track.scrollLeft);
        const max = track.scrollWidth - track.clientWidth;
        let next = offset + direction * step;
        if (direction > 0 && offset >= max - 2) next = 0;
        if (direction < 0 && offset <= 2) next = max;
        track.scrollTo({left: Math.max(0, Math.min(max, next)) * (rtl ? -1 : 1), behavior: reducedMotion.matches ? 'instant' : 'smooth'});
      };
      panel.querySelector('[data-mini-prev]').addEventListener('click', () => move(-1));
      panel.querySelector('[data-mini-next]').addEventListener('click', () => move(1));
    });
  }

  const reviewCleanups = new Map();
  function initReviews(root) {
    root.querySelectorAll('[data-review-carousel]').forEach(section => {
      if (reviewCleanups.has(section)) return;
      const controls = section.querySelector('[data-review-controls]');
      const toggle = section.querySelector('[data-review-toggle]');
      const controller = new AbortController();
      const on = (target, name, handler) => target.addEventListener(name, handler, {signal: controller.signal});
      let paused = reducedMotion.matches;
      const update = () => {
        section.classList.toggle('is-paused', paused);
        controls.hidden = false;
        toggle.textContent = paused ? 'Play reviews' : 'Pause reviews';
        toggle.setAttribute('aria-pressed', String(paused));
      };
      on(toggle, 'click', () => { paused = !paused; update(); });
      on(reducedMotion, 'change', () => { paused = reducedMotion.matches; update(); });
      update();
      reviewCleanups.set(section, () => controller.abort());
    });
  }  function init(root = document) { initHeader(root); initProducts(root); initCardAdds(root); initCartConfirmation(); initMotion(root); initImageMotion(root); initScrollProgress(); initReviews(root); initMiniPerfumes(root); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    reviewCleanups.forEach((cleanup, section) => { if (event.target.contains(section)) { cleanup(); reviewCleanups.delete(section); } });
    event.target.querySelectorAll('[data-motion-ready]').forEach(element => revealObserver?.unobserve(element));
    animations.forEach(animation => { if (event.target.contains(animation.effect?.target)) animation.cancel(); });
  });
  window.addEventListener('pageshow', event => { if (event.persisted) location.reload(); });
})();
